/**
 * Add or change a blood pressure reading on the website: date (DD/MM/YYYY),
 * time, top / bottom numbers, pulse and a note. Used by the ❤️ BP page
 * ("Add a reading") and by "Edit" on each reading (owner: any reading;
 * others: the readings they saved). The level (Normal / High …) is worked
 * out from the family's BP levels.
 */
import { useState, type FormEvent } from 'react';
import { bpLevel, localTodayISO, type BpLimits } from '@bhorosha/core';
import type { BpReading } from '@bhorosha/data';
import { backend } from '../data';

const two = (n: number) => String(n).padStart(2, '0');
function localParts(iso?: string): { date: string; time: string } {
  const d = iso ? new Date(iso) : new Date();
  return { date: `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`, time: `${two(d.getHours())}:${two(d.getMinutes())}` };
}

export function BpForm({ familyId, personId, limits, reading, onSaved, onCancel }: {
  familyId: string; personId: string; limits: BpLimits; reading?: BpReading; onSaved: () => void; onCancel?: () => void;
}) {
  const start = localParts(reading?.measuredAt);
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState(start.time);
  const [sys, setSys] = useState(reading ? String(reading.systolic) : '');
  const [dia, setDia] = useState(reading ? String(reading.diastolic) : '');
  const [pulse, setPulse] = useState(reading?.pulse ? String(reading.pulse) : '');
  const [note, setNote] = useState(reading?.note || '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const today = localTodayISO();
  const s = parseInt(sys, 10), d = parseInt(dia, 10);
  const preview = s >= 40 && s <= 300 && d >= 20 && d <= 200 ? bpLevel(s, d, limits) : null;

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr('');
    const p = pulse ? parseInt(pulse, 10) : null;
    if (!(s >= 40 && s <= 300) || !(d >= 20 && d <= 200)) { setErr('Check the numbers: top 40–300, bottom 20–200.'); return; }
    if (p !== null && !(p >= 20 && p <= 250)) { setErr('Pulse should be between 20 and 250.'); return; }
    if (!date || !/^\d{2}:\d{2}$/.test(time)) { setErr('Please give the date and time of the reading.'); return; }
    const [y, mo, da] = date.split('-').map(Number); const [h, mi] = time.split(':').map(Number);
    const when = new Date(y, mo - 1, da, h, mi);
    if (when.getTime() > Date.now() + 5 * 60000) { setErr('The date and time are in the future.'); return; }
    setBusy(true);
    try {
      await backend.saveBp(familyId, {
        patientId: personId, id: reading?.id || crypto.randomUUID(), measuredAt: when.toISOString(),
        systolic: s, diastolic: d, pulse: p, level: bpLevel(s, d, limits), note: note.trim() || null,
        arm: reading?.arm ?? null, position: reading?.position ?? null, loggedFrom: reading?.loggedFrom || 'web',
      });
      if (!reading) { setSys(''); setDia(''); setPulse(''); setNote(''); const n = localParts(); setDate(n.date); setTime(n.time); }
      onSaved();
    } catch (ex) { setErr(ex instanceof Error ? ex.message : String(ex)); } finally { setBusy(false); }
  }

  return (
    <form className={reading ? 'bh-bp-form' : 'bh-box bh-bp-form'} onSubmit={submit}>
      {!reading && <h4>Add a reading</h4>}
      <div className="bh-bp-when">
        <label>Date<input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} required /></label>
        <label>Time<input type="time" value={time} onChange={e => setTime(e.target.value)} required /></label>
      </div>
      <div className="bh-bp-inputs">
        <label>Top<input inputMode="numeric" value={sys} onChange={e => setSys(e.target.value.replace(/\D/g, ''))} required placeholder="120" /></label>
        <label>Bottom<input inputMode="numeric" value={dia} onChange={e => setDia(e.target.value.replace(/\D/g, ''))} required placeholder="80" /></label>
        <label>Pulse<input inputMode="numeric" value={pulse} onChange={e => setPulse(e.target.value.replace(/\D/g, ''))} placeholder="72" /></label>
      </div>
      <label className="bh-field"><span className="bh-field-label">Note (optional)</span><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. after dialysis, left arm" /></label>
      {preview && <div className={`bh-st bp-${preview}`} style={{ display: 'inline-block', margin: '2px 0 8px' }}>{{ normal: 'Normal', high: 'A bit high', very_high: 'Very high', low: 'Low' }[preview]}</div>}
      {err && <div className="bh-error">{err}</div>}
      <div className="bh-row">
        <button className="bh-btn bh-btn-primary" disabled={busy}>{busy ? 'Saving…' : reading ? 'Save changes' : 'Save reading'}</button>
        {onCancel && <button type="button" className="bh-btn bh-btn-ghost" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}
